import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-slate-50">
      <div className="hidden lg:flex flex-1 relative bg-slate-900 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1618642542397-ef97a739f1d7?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 to-slate-900" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white text-slate-900 rounded flex items-center justify-center font-display font-extrabold">L+</div>
            <div>
              <div className="font-display font-bold text-xl leading-none">Leprecon+ CRM</div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-300 mt-1">Operations Platform</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-slate-300 mb-3 font-mono">v1.0 · Feb 2026</div>
            <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight max-w-md">
              From pre-order to packed.<br />
              <span className="text-slate-400">In one workflow.</span>
            </h1>
            <p className="mt-6 text-sm text-slate-300 max-w-md leading-relaxed">
              Multi-station CRM for luxury car-cover manufacturing. Shopify intake, factory codes, production batches, shipping — unified.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h2 className="font-display text-2xl font-bold mb-1">Sign in</h2>
          <p className="text-sm text-slate-500 mb-6">Access your station dashboard.</p>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Email or Phone</Label>
              <Input
                data-testid="login-email"
                type="text"
                autoComplete="username"
                placeholder="you@example.com or +1 555 123 4567"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <Button data-testid="login-submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800" type="submit">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
